import type { TaskPrStatus } from './types/tasks';

/**
 * A task's pull requests as GitHub sees them now (T-3): the card shows each one's state and merges it
 * when it can. The worker asks GitHub (services/github/pulls.ts); this file is what both sides agree on.
 */

/** owner/name and number of a github.com pull request address. */
export interface GithubPrRef {
  owner: string;
  repo: string;
  number: number;
}

/** `https://github.com/<owner>/<repo>/pull/<n>` (anything after the number ignored), else null. */
export function parseGithubPrUrl(url: string): GithubPrRef | null {
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    return null;
  }
  if (parsed.hostname.toLowerCase() !== 'github.com' && parsed.hostname.toLowerCase() !== 'www.github.com') return null;
  const m = /^\/([A-Za-z0-9-]+)\/([A-Za-z0-9._-]+)\/pull\/(\d+)(?:\/.*)?$/.exec(parsed.pathname);
  if (!m) return null;
  const number = Number(m[3]);
  if (!Number.isSafeInteger(number) || number < 1) return null;
  return { owner: m[1], repo: m[2], number };
}

/**
 * GitHub's `mergeable_state`: clean (ready), unstable (non-required checks failing, still mergeable),
 * has_hooks (mergeable), dirty (conflicts), blocked (required reviews or checks), behind (the base
 * needs the branch up to date), draft, unknown (still working it out).
 */
export type GithubMergeableState = 'clean' | 'unstable' | 'has_hooks' | 'dirty' | 'blocked' | 'behind' | 'draft' | 'unknown';

/** One pull request, live. */
export interface GithubPrLive {
  number: number;
  title: string;
  state: 'open' | 'closed';
  merged: boolean;
  draft: boolean;
  /** null while GitHub is still working it out (it does on the first read after a push). */
  mergeable: boolean | null;
  mergeable_state: GithubMergeableState;
  /** The commit a merge would take; the merge is refused if the branch moved since. */
  head_sha: string;
  /** SF-T-79: the PR's branch, its base branch, and the base's commit GitHub compared against. */
  head_ref: string;
  /** T-110: the repo the PR's branch is in (owner/name): another one's for a fork. */
  head_repo?: string;
  base_ref: string;
  base_sha: string;
}

/**
 * SF-T-79: the checks on a ready pull request's head (the factory's Local checks, and Actions): failure
 * when any failed, pending while any is going, success when all passed, none when it has none.
 */
export interface PrChecksSummary {
  state: 'pending' | 'success' | 'failure' | 'none';
  /** The checks' names that failed, and those still going. */
  failing: string[];
  pending: string[];
  /** The checks the repo requires (`checks.required` in its devkit.config.json) that haven't run on it at all. */
  missing?: string[];
}

/** A task's pull request on GET /api/tasks/:taskId/prs. */
export interface TaskPrLive {
  url: string;
  /** null: not a github.com pull request, GitHub isn't connected, or the read failed (`error`). */
  live: GithubPrLive | null;
  /** Why there's no live state, in words for the card's hint. */
  error: string | null;
  /** SF-T-79: its checks, read only when it's otherwise ready to merge (null otherwise, or when they couldn't be read). */
  checks?: PrChecksSummary | null;
  /**
   * SF-T-154: on the parent of sub-tasks sharing this PR, those not ready for it to be merged (Merge stays
   * off until there are none). Absent for a PR no sub-tasks share.
   */
  subtasks_not_ready?: SharedPrSubtask[];
  /**
   * SF-T-183: a ready PR that adds migrations and can't be merged yet for them (main has a newer one, or two
   * heads). Absent when nothing holds it.
   */
  migrations?: PrMigrationsBlock | null;
}

/**
 * SF-T-183: why a migration PR can't be merged from the card. `behind`: main gained migration folders since
 * the PR's merge base (`folders`; a PR so far behind GitHub cut the list off has none named); Update hands
 * it to the AI builder. `forked`: main has two or more migration heads (`folders`) until a merge migration
 * joins them.
 */
export interface PrMigrationsBlock {
  reason: 'migrations_behind' | 'main_forked';
  folders: string[];
}

/** SF-T-154: a sub-task sharing its parent's PR that isn't ready for it to be merged (or the parent, with findings to settle), and why. */
export interface SharedPrSubtask {
  task_id: string;
  /** "SF-T-137". */
  ref: string;
  /** "in In progress", "waits on Andrew", "the AI builder has it", "1 disputed finding to settle". */
  reasons: string[];
}

/** SF-T-154: why a shared PR can't be merged yet: "Not ready to merge: SF-T-137 (in In progress, waits on Andrew)." */
export function subtasksNotReadyText(subtasks: SharedPrSubtask[]): string {
  const list = subtasks.map((s) => `${s.ref} (${s.reasons.join(', ')})`).join('; ');
  return `Not ready to merge: ${list}. Each sub-task sharing it must be in Review or done, with nobody waiting on a person, and every disputed finding settled.`;
}

export interface TaskPrsLiveResponse {
  /** False when the hub has no GitHub token: the card shows the stored status alone. */
  github_connected: boolean;
  prs: TaskPrLive[];
}

/** Which way the card shows a live pull request, and whether its Merge button is on. */
export type PrMergeView =
  | 'merged'
  | 'closed'
  | 'draft'
  | 'conflicts'
  | 'blocked'
  | 'behind'
  | 'checking'
  | 'ready';

export function prMergeView(pr: Pick<GithubPrLive, 'state' | 'merged' | 'draft' | 'mergeable' | 'mergeable_state'>): PrMergeView {
  if (pr.merged) return 'merged';
  if (pr.state === 'closed') return 'closed';
  if (pr.draft || pr.mergeable_state === 'draft') return 'draft';
  if (pr.mergeable === false || pr.mergeable_state === 'dirty') return 'conflicts';
  if (pr.mergeable === null || pr.mergeable_state === 'unknown') return 'checking';
  if (pr.mergeable_state === 'blocked') return 'blocked';
  if (pr.mergeable_state === 'behind') return 'behind';
  return 'ready';
}

/** Only a ready pull request can be merged from the card. */
export function canMergeFromCard(view: PrMergeView): boolean {
  return view === 'ready';
}

/**
 * SF-T-79: why a ready pull request's checks stop it being merged, or null when they don't. None may be
 * failing or still going, and there must be some (a branch pushed without the pre-push hook has no Local
 * checks): GitHub can't require the factory's Local checks, so the factory does.
 */
export type ChecksBlock = 'checks_failing' | 'checks_pending' | 'checks_missing' | 'checks_none' | 'checks_unknown';
export function checksBlockMerge(checks: PrChecksSummary | null | undefined): ChecksBlock | null {
  if (!checks) return 'checks_unknown';
  if (checks.state === 'failure') return 'checks_failing';
  if (checks.state === 'pending') return 'checks_pending';
  // The repo names checks it needs (software-factory: Local checks): others passing doesn't stand in for them.
  if (checks.missing?.length) return 'checks_missing';
  if (checks.state === 'none') return 'checks_none';
  return null;
}

/** What Merge says when it's off, for each reason (the card's and the task panel's hint). */
export function mergeBlockedText(reason: PrMergeView | ChecksBlock | PrMigrationsBlock['reason'], checks?: PrChecksSummary | null, folders: string[] = []): string {
  const named = folders.length ? ` (${folders.slice(0, 3).join(', ')}${folders.length > 3 ? ` and ${folders.length - 3} more` : ''})` : '';
  switch (reason) {
    case 'migrations_behind':
      return `main has a newer migration${named}: update this PR first.`;
    case 'main_forked':
      return `main has two migration heads${named}: merge the fix first.`;
    case 'checks_missing':
      return `${checks?.missing?.join(', ') || 'A check the repo requires'} hasn't run on it (the repo's devkit.config.json requires it). Run it on the branch first.`;
    case 'checks_none':
      return 'No checks have run on it (Local checks posts when the branch is pushed with its hook). Run them first.';
    case 'checks_failing':
      return `Its checks failed${checks?.failing.length ? ` (${checks.failing.join(', ')})` : ''}. Fix them first.`;
    case 'checks_pending':
      return `Its checks are still going${checks?.pending.length ? ` (${checks.pending.join(', ')})` : ''}. Merge once they pass.`;
    case 'checks_unknown':
      return "Its checks couldn't be read. Try again in a moment.";
    case 'conflicts':
      return 'It has conflicts with its base branch. Resolve them first.';
    case 'blocked':
      return 'GitHub is blocking the merge (required reviews or checks).';
    case 'behind':
      return 'The branch is behind its base. Update it first.';
    case 'draft':
      return "It's a draft.";
    case 'closed':
      return "It's closed.";
    case 'merged':
      return "It's merged already.";
    case 'checking':
      return 'GitHub is still checking whether it can merge. Try again in a moment.';
    default:
      return '';
  }
}

/** SF-T-79: the checks, summed up for the card and the merge rule. */
export function summariseChecks(
  read: { state: PrChecksSummary['state']; checks: Array<{ name: string; state: 'pending' | 'success' | 'failure' }> },
  /** The checks the repo requires by name (none: any checks will do). */
  required: string[] = []
): PrChecksSummary {
  const missing = required.filter((name) => !read.checks.some((c) => c.name === name));
  return {
    state: read.state,
    failing: read.checks.filter((c) => c.state === 'failure').map((c) => c.name),
    pending: read.checks.filter((c) => c.state === 'pending').map((c) => c.name),
    ...(missing.length ? { missing } : {}),
  };
}

/**
 * The status a task keeps for a pull request GitHub reports this way (T-13): what the card shows
 * without a live read, and what the Board's PR filters use.
 */
export function storedPrStatus(pr: Pick<GithubPrLive, 'state' | 'merged' | 'draft'>): TaskPrStatus {
  if (pr.merged) return 'merged';
  if (pr.state === 'closed') return 'closed';
  return pr.draft ? 'draft' : 'open';
}

/** The same pull request, whatever the address's case or what follows the number. */
export function sameGithubPr(a: GithubPrRef, b: GithubPrRef): boolean {
  return a.number === b.number && a.owner.toLowerCase() === b.owner.toLowerCase() && a.repo.toLowerCase() === b.repo.toLowerCase();
}
